<script>
import {mapGetters} from "vuex";
import {
    SET_FLOORS,
    SET_JSON_CONFIG_DATA,
    SET_KIOSK,
    SET_LABELS,
    SET_MODELS,
    SET_NAMES,
    SET_PORTALS,
    SET_UNITS
} from "./constants/map-commands";
import { customPropFlagsMapping } from "./constants/icon-props";
import PLACEMENT_POINTS_TYPES from "./constants/placement-points-types";

export default {
    name: 'MvDataLayer',
    props:{
        mapData: { type: Object, required: true },
    },
    computed:{
        ...mapGetters(['getFileSrc', 't', 'getUnitPlacementPoints']),

        isOfflineMode(){
            return process.env.VUE_APP_OFFLINE_MODE;
        },
        mvConfig(){
            return {
                ...this.mapData.config,
                floors: {
                    ...this.mapData.config.floors,
                    ...this.mvConfigFloors
                },
                //labels: this.mvConfigLabels
            }
        },
        appTheme(){
            return this.mapData.config.name;
        },
        mvModels(){
            return this.mapData.models.map( m => {
                const files = m.filesSrc || m.files;

                return {
                    ...m,
                    files: files, // TODO: fix terminal(m.files) & manager apps inconsistency(m.filesSrc)
                }
            })
        },
        mvConfigFloors(){
            return this.mvFloors.reduce((acc,f) => {
                acc[f.unit] = {
                    ..._.get( this.mapData.config, `floors[${f.unit}]`, {}),
                    ...f
                };
                return acc
            },{})
        },
        mvFloors(){
            return this.mapData.objects.reduce( (acc, obj) => {
                if(obj.type === 'floor'){
                    acc.push({
                        id: obj.id,
                        map_model_id: obj.unit.map_model_id,
                        unit: obj.unit.name,
                        level: obj.meta.level,
                        building_index: obj.meta.building_index,
                        building_group_id: obj.floor.building_group_id,
                        is_outdoor: obj.meta.is_outdoor || false,
                        ...(obj.meta.settings || {}),
                    })
                }
                return acc
            },[])
        },
        mvPortals(){
            return Object.values(
                this.mapData.objects.reduce( (acc, obj) => {
                    if(obj.type !== 'portal') return  acc

                    if(acc[obj.id])
                        acc[obj.id].units.push(obj.unit.name)
                    else
                        acc[obj.id] = {
                            id: obj.id,
                            map_model_id: obj.unit.map_model_id,
                            name: this.t(obj,'name'),
                            portal_type: obj.meta.portal_type,
                            direction: obj.meta.portal_direction,
                            active: obj.meta.active || false,
                            anchor: obj.meta.anchor || false,
                            dont_show_step: obj.meta.dont_show_step || false,
                            custom_message: obj.meta.custom_message || null,
                            target_zone: obj.lists.target_zone || null,
                            units: [obj.unit.name],
                            ...(obj.meta.settings || {}),
                        }
                    return acc
                },{})
            )
        },
        mvUnits(){
            return this.mapData.units.map(u => ({
                map_model_id: u.map_model_id,
                unit: u.name,
                floor: u.floor.unit_name,
                can_pass_through: u.type === 'virtual_unit',
            }))
        },
        mvObjects(){
            return this.mapData.objects.reduce((acc, obj) => {
                if(!['tenant','service','terminal','portal','poster','qrcode'].includes(obj.type)) return  acc;

                const preferredPortals =
                    obj.lists.target_zone ?
                        this.mvPortals.reduce( (accc, portal) => {
                            // prefer to us portals for same TZ
                            portal.target_zone === obj.lists.target_zone && accc.push(portal.id);
                            return accc;
                        }, []) : [];

                const o = {
                    id: obj.id,
                    map_model_id: obj.unit.map_model_id,
                    unit: obj.unit.name,
                    name: this.t(obj, 'name'),
                    anchor: obj.meta.anchor || false,
                    active: obj.meta.active,
                    landmark: this.t(obj, 'landmark', false),
                    type: obj.type,
                    floor: obj.floor.unit_name,
                    destination_step_message: this.t(obj, 'destination_step_message'),
                    destination_step_icon: obj.files.destination_step_icon,
                    show_tenant_name_in_map: true,//obj.meta.show_tenant_name_in_map || false,
                    target_zone: obj.lists.target_zone,
                    preferred_portals: preferredPortals,
                    can_pass_through: Boolean(obj.meta.can_pass_through) || obj.unit.type === 'virtual_unit',
                };
                acc.push(o);
                return acc
            },[])
        },
        labelsLoadFilter(){
            return this.getConfigProp('map_labels.load', {});
        },
        shouldLoadLabelsAndAnchors(){
            return this.labelsLoadFilter.labels && this.labelsLoadFilter.anchors;
        },
        mvLabels(){
            //TODO: re-design && refactor;

            return this.mapData.objects.reduce( (acc, obj) => {
                // Check should we show icon in this app
                const shouldShowInCurrentApp = (o) =>
                    !o.meta.visible_in_apps ||
                    o.meta.visible_in_apps.length === 0 ||
                    o.meta.visible_in_apps.includes('all') ||
                    o.meta.visible_in_apps.includes(this.appTheme);

                const shouldShowLabel =
                    obj.meta.show_label ||
                    obj.type === 'tenant' ||
                    (obj.meta.display_on_map_as && obj.meta.display_on_map_as !== 'none')

                if(
                    shouldShowLabel &&
                    shouldShowInCurrentApp(obj) &&
                    ['tenant', 'service', 'label', 'portal'].includes(obj.type)
                ){

                    const labelData = {
                        ...this.getObjectLabelTextData(obj),
                        ...this.getObjectLabelIconData(obj),
                        ...this.getLabelLegacyData(obj),
                        ...this.getLabelData(obj),
                    }

                    if(!_.isEmpty(labelData)){
                        acc.push({
                            object_type: obj.type,
                            uid: `${obj.id}--${obj.unit.name}`,
                            id: obj.id,
                            where:'unit',
                            unit: obj.unit.name,
                            anchor: obj.meta.anchor,
                            group: obj.floor.building_group_id,
                            ...labelData,
                        })
                    }
                }
                return acc
            }, [])
        }

    },
    inject:['mapViewer'],

    mounted(){
        this.initViewer()

    },
    methods:{
        initViewer(){
            this.setModels();
            this.setFloors();
            this.setNames();
            this.setUnits();
            this.setPortals();
            this.setMapConfig();
            this.setKiosk();
            this.setLabels();
        },
        getObjectLabelTextData(object){
            return this.isObjectLabelText(object) ? { text: this.t(object,'name') } : {};
        },
        getObjectLabelIconData(object, slug = 'icon'){
            const icon = this.t(object, 'icon2') || this.t(object, slug);
            return this.isObjectLabelIcon(object, slug) && !this.isObjectLabelText(object) ? { url: this.getIconUrl(icon) } : {};
        },
        isObjectLabelIcon(object, slug = 'icon'){
            const isDisplayOnMapAsImage = object.meta.display_on_map_as === 'image';
            if(isDisplayOnMapAsImage) return true;

            const isDisplayOnMapAsText = object.meta.display_on_map_as === 'text';
            if(isDisplayOnMapAsText) return false;

            const icon = this.t(object, 'icon2') || this.t(object, slug);
            const shouldLoadIcon = !this.shouldLoadLabelsAndAnchors || object.type === 'label' || object.meta.anchor;

            const shouldUseIcon = icon && !object.meta.show_tenant_name_in_map;
            return shouldLoadIcon && shouldUseIcon;
        },
        isObjectLabelText(object){
            const isDisplayOnMapAsText = object.meta.display_on_map_as === 'text';
            if(isDisplayOnMapAsText) return true;

            const isDisplayOnMapAsImage = object.meta.display_on_map_as === 'image';
            if(isDisplayOnMapAsImage) return false;

            const hasIcon = object.files.icon2 || object.files.icon

            const shouldShowTenantText = object.type === 'tenant' && (!object.meta.anchor || !hasIcon);
            const shouldShowLabelText = object.type === 'label' && !object.files.icon;
            const shouldShowText = object.meta.show_tenant_name_in_map;

            return shouldShowTenantText ||
                shouldShowLabelText ||
                shouldShowText
        },
        getIconUrl(url){
            let newUrl = typeof url === 'string' ? url : this.getFileSrc(url);
            if(!url || !newUrl) {
                return null;
            }

            if(this.isOfflineMode){
                /* eslint-disable-next-line no-useless-escape */
                newUrl = newUrl.replace(/(.*\/\/[^\/]*\/).*\/(.*)\.svg(\??.*)$/, '$1temp-png/$2.png');
            }
            else {
                newUrl = newUrl.replace(/(.*mvgcloud.com\/|.*mvg-toolbox.com\/|.*cloudfront.net\/|\/).*\/(.*)\.svg$/, '$1temp-png/$2.png');
            }

            newUrl += '?mode=texture';
            return newUrl;
        },
        getLabelData (object) {
            let result = {}
            const iconData = _.get(object.meta, `icons2[${object.unit.name}]`) || _.get(object.meta, `icons[${object.unit.name}]`);

            if(iconData){
                Object.entries(customPropFlagsMapping).forEach(([propKey, propFlag] ) => {
                    if(!iconData[propFlag]) return;

                    const iconProp = iconData[propKey];

                    if(
                        typeof iconProp !== 'undefined' &&
                        iconProp !== null
                    ) {
                        result[propKey] = iconProp
                    }
                })

                if (iconData.custom_icon_position){
                    result.unit_offset = {
                        x: iconData.icon_position_x,
                        y: iconData.icon_position_y,
                        z: iconData.icon_position_z,
                    };
                }
            }

            //we need combine_with_placement_point_setting to avoid unexpected mix of settings
            const shouldCombineWithPlacementPointData = _.isEmpty(result) || ( !_.isEmpty(result) && iconData.custom_placement_point);
            if(shouldCombineWithPlacementPointData){
                const placementPointData = this.getPlacementPointData(object, iconData);
                if(placementPointData) {
                    result = {...placementPointData.props, ...result}
                }

            }

            if(result.unit_offset){
                result.position_offset = result.unit_offset;
            }

            return result
        },
        getPlacementPointData(object, iconData){
            const placementPoints = object.unit && object.unit.placement_points || this.getUnitPlacementPoints(object.unit.id);
            const shouldIgnorePlacementPoints = iconData && iconData.placement_point === 'none';

            if(
                placementPoints &&
                placementPoints.length > 0 &&
                !shouldIgnorePlacementPoints
            ) {
                const hasCustomPlacementPoint = iconData && iconData.custom_placement_point && iconData.placement_point;

                if(hasCustomPlacementPoint){
                    return placementPoints.find( p => p.id === iconData.placement_point)
                }

                const placementPointsType = this.getPlacementPointsType(object);
                if(placementPointsType){
                    return placementPoints.find( p => p.type === placementPointsType);
                }
            }
        },
        getLabelLegacyData (object) {
            const settings = { ...(object.meta.settings || {}) };
            if (settings.position)
                settings.position_offset = {
                    x: settings.position.x,
                    y: settings.position.y,
                    z: settings.position.z,
                }
            return settings;
        },
        getConfigProp( path, def = null) {
            return _.get(this.mapData.config, path, def)
        },

        // Data supply commands
        setModels() {
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_MODELS, this.mvModels)
        },
        setFloors() {
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_FLOORS, this.mvFloors);
        },
        setNames() {
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_NAMES, this.mvObjects);
        },
        setLabels() {
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_LABELS, this.mvLabels);
        },
        setUnits() {
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_UNITS, this.mvUnits);
        },
        setPortals(){
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_PORTALS, this.mvPortals);
        },
        setMapConfig(){
            if(!this.mapViewer.listenEvents) return
            this.mapViewer.sendMapCommand(SET_JSON_CONFIG_DATA, this.mvConfig);
        },
        setKiosk(){
            if(this.mapData.terminalUnitName)
                this.mapViewer.sendMapCommand(SET_KIOSK, {
                    unit: this.mapData.terminalUnitName,
                });
        },
        getPlacementPointsType(object) {
            if(this.isObjectLabelText(object)) return PLACEMENT_POINTS_TYPES.text
            if(this.isObjectLabelIcon(object)) return PLACEMENT_POINTS_TYPES.image

            return null;
        }
    },
    watch:{
        mvConfig: {
            handler() {
                if(this.mapViewer.isReactive)
                    this.setMapConfig()
            },
        },
        mvObjects: {
            handler() {
                if(this.mapViewer.isReactive)
                    this.setNames()
            },
        },
        mvLabels: {
            handler() {
                if(this.mapViewer.isReactive)
                    this.setLabels()
            },
        },
        'mapData.config.cameras'(config){
            const conf = JSON.parse(JSON.stringify(config));
            this.mapViewer.viewApi && this.mapViewer.viewApi.config.from_json(conf);
        },
    },
    render(){ return null }
}
</script>

